import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { connect } from 'react-redux'

import MainHeader from '../../../ReusableComponents/Header/MainHeader'
import { getConnectedDeviceImage } from '../../../../hooks/useBrandConfig'
import { languageWords } from '../../../../utils/langaugeSupport'
import { zingFreeFallSendBytes } from '../../../../utils/freeFallBytes'
import { ZingByteCodes } from '../../../ReusableComponents/ByteTransfer/byteCodes'
import {
    ZING_PLAY_COMMAND,
    ZingPlayFrames,
    buildZingPlayFrame,
    isZingPlayFrameVersion,
    GET_ALL_DANCE_NAMES_FRAME,
    parseGetAllDanceNamesFrame,
} from '../../../ReusableComponents/ByteTransfer/zingPlayFrame'
import zingImage from '../../../../Assets/humanoid/ZingImage.png'
import zingImage3 from '../../../../Assets/humanoid/ZingImage3x.png'
import playRunBtn from '../../../../Assets/Bisoft_UI/Buttons/Active with shadow/play-run@2x.png'
import pauseBtn from '../../../../Assets/Bisoft_UI/Buttons/Active with shadow/pause_btn@3x.png'
import styles from './ZingMusic.module.css'

// Used while disconnected (not rendered — a connect message is shown instead)
// and for connected firmware that doesn't support GET_ALL_DANCE_NAME, where
// it's the actual fixed Dance 1-4 list.
const PLACEHOLDER_DANCES = [1, 2, 3, 4].map((n) => ({
    id: n,
    name: `Dance ${n}`,
    isPlaceholder: true,
}))
const ATTENTION_SETTLE_MS = 1500
const DANCE_MAX_MS = 92500
const ASTOP_SETTLE_MS = 1000
const DANCE_FETCH_TIMEOUT_MS = 4000
const DANCE_FETCH_MAX_ATTEMPTS = 3
const RDONE_GUARD_MS = 3000
const PLAY_BYTES = ['P', 'L', 'A', 'Y'].map((c) => c.charCodeAt(0))
const ASTOP_BYTES = ['A', 'S', 'T', 'O', 'P'].map((c) => c.charCodeAt(0))

const VibrationMarks = ({ side, playing }) => {
    const gradientId = `zingMarksGradient-${side}`
    return (
        <svg
            className={`${styles.marks} ${side === 'left' ? styles.marksLeft : styles.marksRight}`}
            style={playing ? { stroke: `url(#${gradientId})` } : undefined}
            viewBox="0 0 50 54"
            aria-hidden="true"
        >
            <defs>
                <linearGradient
                    id={gradientId}
                    gradientUnits="userSpaceOnUse"
                    x1="50"
                    y1="0"
                    x2="0"
                    y2="54"
                >
                    <stop offset="0" stopColor="#fda94d" />
                    <stop offset="1" stopColor="#f87e61" />
                </linearGradient>
            </defs>
            <polyline points="6,8 28,8 14,22 42,22" />
            <polyline points="2,32 24,32 10,46 38,46" />
        </svg>
    )
}

function ZingMusic({ webSerial, worker, device }) {
    const history = useHistory()
    const connectedDevice = sessionStorage.getItem('connectedDevice')
    const deviceVersion = sessionStorage.getItem('deviceVersion')
    const isFramed = isZingPlayFrameVersion(connectedDevice, deviceVersion)
    const sendsAStop =
        parseInt(deviceVersion?.split('.')?.[0] ?? '0') >= 3 ||
        parseInt(deviceVersion?.split('.')?.[2] ?? '0') >= 6
    const languageName = device?.language

    const [selected, setSelected] = useState(PLACEHOLDER_DANCES[0].id)
    const [isPlaying, setIsPlaying] = useState(false)
    const isPlayingRef = useRef(false)
    const sendTokenRef = useRef(0)
    const doneTimerRef = useRef(null)
    const lastCommandAtRef = useRef(0)

    const [dances, setDances] = useState(PLACEHOLDER_DANCES)
    const [fetchStatus, setFetchStatus] = useState('idle') // idle | loading | ready | empty | error
    const awaitingDanceResponseRef = useRef(false)
    const hasFetchedForConnectionRef = useRef(false)
    const danceFetchTimeoutRef = useRef(null)
    const danceFetchAttemptRef = useRef(0)

    const write = (data) => {
        if (!webSerial.isConnected) return
        worker.postMessage({ type: 'writeArray', value: data })
    }

    const danceBytes = (n) =>
        isFramed ? ZingPlayFrames[`DANCE${n}`] : ZingByteCodes[`DANCE${n}`]
    const attentionBytes = isFramed
        ? ZingPlayFrames.ATTENTION
        : ZingByteCodes.ATTENTION

    const danceCommandBytes = (entry) => {
        if (isFramed && !entry.isPlaceholder) {
            return buildZingPlayFrame(ZING_PLAY_COMMAND.DO_DANCE, [entry.id])
        }
        return danceBytes(entry.id)
    }

    // token, when given, must match the latest action or the call is stale.
    const markDone = (token) => {
        if (token !== undefined && token !== sendTokenRef.current) return
        clearTimeout(doneTimerRef.current)
        if (!isPlayingRef.current) return
        isPlayingRef.current = false
        setIsPlaying(false)
        zingFreeFallSendBytes({ worker, value: 1 })
    }

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

    // Returns false if a newer action superseded this one while waiting.
    const sendStop = async (token) => {
        if (sendsAStop) {
            write(ASTOP_BYTES)
            await sleep(ASTOP_SETTLE_MS)
            if (token !== sendTokenRef.current) return false
        }
        write(attentionBytes)
        return true
    }

    const requestDanceNames = () => {
        if (!webSerial.isConnected || !isFramed) return
        clearTimeout(danceFetchTimeoutRef.current)
        awaitingDanceResponseRef.current = true
        setFetchStatus('loading')
        worker.postMessage({
            type: 'writeZingPlayFrame',
            value: GET_ALL_DANCE_NAMES_FRAME,
        })
        danceFetchTimeoutRef.current = setTimeout(() => {
            awaitingDanceResponseRef.current = false
            if (danceFetchAttemptRef.current < DANCE_FETCH_MAX_ATTEMPTS - 1) {
                danceFetchAttemptRef.current += 1
                requestDanceNames()
                return
            }
            setFetchStatus('error')
        }, DANCE_FETCH_TIMEOUT_MS)
    }

    const playDance = async (entry) => {
        if (
            isFramed &&
            webSerial.isConnected &&
            (fetchStatus === 'loading' || fetchStatus === 'error')
        )
            return

        setSelected(entry.id)
        if (!webSerial.isConnected) return
        const token = ++sendTokenRef.current

        if (isPlayingRef.current) {
            if (!(await sendStop(token))) return
            await sleep(ATTENTION_SETTLE_MS)
        } else {
            zingFreeFallSendBytes({ worker, value: 0 })
            await sleep(100)
        }
        if (token !== sendTokenRef.current) return

        isPlayingRef.current = true
        setIsPlaying(true)
        write(danceCommandBytes(entry))
        lastCommandAtRef.current = Date.now()
        clearTimeout(doneTimerRef.current)
        doneTimerRef.current = setTimeout(() => markDone(token), DANCE_MAX_MS)
    }

    const stopDance = async () => {
        const token = ++sendTokenRef.current
        lastCommandAtRef.current = Date.now()
        clearTimeout(doneTimerRef.current)
        isPlayingRef.current = false
        setIsPlaying(false)
        if (!webSerial.isConnected) return
        if (await sendStop(token)) zingFreeFallSendBytes({ worker, value: 1 })
    }

    const togglePlay = () => {
        if (isPlaying) {
            stopDance()
            return
        }
        const entry = dances.find((d) => d.id === selected) || dances[0]
        if (entry) playDance(entry)
    }

    useEffect(() => {
        if (!webSerial.isConnected) return
        const t = setTimeout(() => write(PLAY_BYTES), 200)
        return () => clearTimeout(t)
    }, [webSerial.isConnected])

    useEffect(() => {
        clearTimeout(danceFetchTimeoutRef.current)
        if (!webSerial.isConnected) {
            if (isPlayingRef.current) stopDance()
            awaitingDanceResponseRef.current = false
            hasFetchedForConnectionRef.current = false
            danceFetchAttemptRef.current = 0
            setFetchStatus('idle')
            setDances(PLACEHOLDER_DANCES)
            setSelected(PLACEHOLDER_DANCES[0].id)
            return
        }
        if (!isFramed) return
        if (hasFetchedForConnectionRef.current) return
        hasFetchedForConnectionRef.current = true
        danceFetchAttemptRef.current = 0
        const t = setTimeout(requestDanceNames, 400)
        return () => clearTimeout(t)
    }, [webSerial.isConnected, isFramed])

    useEffect(() => {
        const onMessage = (e) => {
            if (e.data.type === 'read' && typeof e.data.value === 'string') {
                if (/rdone/i.test(e.data.value)) {
                    // ignore stale "done" from a just-stopped/started dance
                    if (Date.now() - lastCommandAtRef.current < RDONE_GUARD_MS)
                        return
                    markDone()
                }
                return
            }
            if (e.data.type !== 'actionRead') return
            if (!awaitingDanceResponseRef.current) return
            const arrayData = e.data.value?.arrayData
            if (!Array.isArray(arrayData)) return
            const parsed = parseGetAllDanceNamesFrame(arrayData)
            if (parsed === null) return // not a GET_ALL_DANCE_NAME frame; ignore

            clearTimeout(danceFetchTimeoutRef.current)
            awaitingDanceResponseRef.current = false

            if (parsed.length === 0) {
                setDances([])
                setFetchStatus('empty')
                return
            }
            const nextDances = parsed.map(({ index, name }) => ({
                id: index,
                name,
                isPlaceholder: false,
            }))
            setDances(nextDances)
            setSelected(nextDances[0].id)
            setFetchStatus('ready')
        }
        worker.addEventListener('message', onMessage)
        return () => {
            worker.removeEventListener('message', onMessage)
            clearTimeout(doneTimerRef.current)
            clearTimeout(danceFetchTimeoutRef.current)
        }
    }, [])

    const retryFetchDances = () => {
        danceFetchAttemptRef.current = 0
        requestDanceNames()
    }

    const goBack = () => {
        sendTokenRef.current++
        write(attentionBytes)
        if (isPlayingRef.current) markDone()
        sessionStorage.setItem('slideDirection', true)
        history.push('/introduction')
    }

    const robotSrc =
        getConnectedDeviceImage(connectedDevice, deviceVersion) ||
        (deviceVersion?.startsWith('3') ? zingImage3 : zingImage)

    const selectedName =
        webSerial.isConnected &&
        dances.find((d) => d.id === selected)?.name

    return (
        <div className={styles.root}>
            <div className={styles.leftPanel} />
            <div className={styles.rightPanel} />

            <MainHeader
                title={languageWords[languageName]?.Music || 'Music'}
                showBluetoothBtn={true}
                goBack={goBack}
            />

            <div className={styles.card}>
                <div
                    className={`${styles.robotWrap} ${isPlaying ? styles.playing : ''}`}
                >
                    <img
                        className={styles.robot}
                        src={robotSrc}
                        alt="Zing"
                        draggable="false"
                    />
                    <VibrationMarks side="left" playing={isPlaying} />
                    <VibrationMarks side="right" playing={isPlaying} />
                </div>
                <button
                    type="button"
                    className={styles.playBtn}
                    onClick={togglePlay}
                    aria-label={
                        isPlaying
                            ? 'Pause dance'
                            : `Play ${selectedName || 'dance'}`
                    }
                >
                    <img
                        src={isPlaying ? pauseBtn : playRunBtn}
                        alt=""
                        draggable="false"
                    />
                </button>
            </div>

            {!webSerial.isConnected ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon} aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                            <path d="M9 18V6l10-2v12" />
                            <circle cx="6.5" cy="18" r="2.5" />
                            <circle cx="16.5" cy="16" r="2.5" />
                        </svg>
                    </div>
                    <p className={styles.emptyTitle}>
                        Connect your Zing to explore available dances
                    </p>
                    <p className={styles.emptySubtext}>
                        Your device&apos;s dance library will appear here once
                        connected.
                    </p>
                </div>
            ) : isFramed &&
              (fetchStatus === 'loading' || fetchStatus === 'idle') ? (
                <div
                    className={styles.emptyState}
                    role="status"
                    aria-live="polite"
                >
                    <div className={styles.emptyIcon} aria-hidden="true">
                        <span className={styles.spinner} />
                    </div>
                    <p className={styles.emptyTitle}>
                        Loading dances from your Zing
                        <span className={styles.dots} aria-hidden="true">
                            <i />
                            <i />
                            <i />
                        </span>
                    </p>
                    <p className={styles.emptySubtext}>
                        Hang tight, fetching the dance list from your device.
                    </p>
                </div>
            ) : fetchStatus === 'empty' ? (
                <p className={styles.statusText}>
                    No dances found on this device.
                </p>
            ) : fetchStatus === 'error' ? (
                <p className={styles.statusText}>
                    Couldn&apos;t load dances from the device.
                    <button
                        type="button"
                        className={styles.retryBtn}
                        onClick={retryFetchDances}
                    >
                        Retry
                    </button>
                </p>
            ) : (
                <ul className={styles.list} aria-busy={fetchStatus === 'loading'}>
                    {dances.map((entry) => (
                        <li key={entry.id} className={styles.row}>
                            <span
                                className={`${styles.handle} ${selected === entry.id ? styles.handleActive : ''}`}
                                aria-hidden="true"
                            >
                                <i />
                                <i />
                                <i />
                            </span>
                            <button
                                type="button"
                                className={`${styles.danceBtn} ${selected === entry.id ? styles.danceBtnActive : ''}`}
                                onClick={() => playDance(entry)}
                                disabled={fetchStatus === 'loading'}
                                title={entry.name}
                            >
                                {entry.name}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

        </div>
    )
}

const mapStateToProps = (state) => state

export default connect(mapStateToProps)(ZingMusic)
